import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { Box, Collapse, List } from '@mui/material';
import CollapsibleMenuItemToggle from 'components/shared/menus/CollapsibleMenuItemToggle';
import SidebarPanelContext, { matchesSearchQuery } from 'context/SidebarPanelContext';
import settingsSlice from 'store/modules/settings';

// Module-level cache: persists open state across remounts (StrictMode, re-renders, etc.)
const openStateCache = new Map();

function CollapsibleMenuItem({
  children,
  icon,
  iconOnly = false,
  label,
  noLink = false,
  path = '/',
  tourKey = null
}) {
  const cacheKey = `${label}__${path}`;
  const match = useMatch(`${path}/*`);
  // path='/' (default for noLink items with no specific path) matches every URL via '/*'.
  // Exclude it so isActive is not permanently true for those items.
  const isActive = path !== '/' && match !== null;

  const { query } = useContext(SidebarPanelContext);
  // Filtro del buscador: el grupo se muestra si su título matchea o si algún hijo matchea;
  // si matchea el título, los hijos se muestran completos (forceVisible).
  const selfMatch = matchesSearchQuery(label, query);
  const childLabels = React.Children.toArray(children)
    .map((child) => child?.props?.label)
    .filter(Boolean);
  const childMatch = childLabels.some((childLabel) => matchesSearchQuery(childLabel, query));
  const filtering = Boolean(query?.trim());
  const visible = !filtering || selfMatch || childMatch;

  const [open, setOpen] = useState(() => openStateCache.get(cacheKey) ?? false);
  const dispatch = useDispatch();
  const settingsId = useSelector((state) => state.auth.user?.settings?.id);

  // Auto-open when a child route becomes active (e.g. navigating directly to a sub-route).
  // Only fires when the user has never interacted with this item (cache === undefined),
  // to avoid fighting with an explicit user close.
  useEffect(() => {
    if (isActive && openStateCache.get(cacheKey) === undefined) {
      openStateCache.set(cacheKey, true);
      setOpen(true);
    }
  }, [isActive, cacheKey]);

  const toggleCollapse = () => {
    // En mini-drawer (iconOnly) los hijos no caben y se ven rotos. Si el usuario abre un acordeón
    // estando colapsado, primero EXPANDIMOS el panel y luego lo abrimos ("si deseo abrir un
    // acordeón que se expanda").
    if (iconOnly) {
      if (settingsId) {
        dispatch(settingsSlice.actions.update({ id: settingsId, input: { mini_drawer: false } }));
      }
      openStateCache.set(cacheKey, true);
      setOpen(true);
      return;
    }
    const next = !open;
    openStateCache.set(cacheKey, next);
    setOpen(next);
  };

  if (!visible) return null;

  // Con filtro activo el grupo se fuerza abierto para mostrar los hijos que matchean.
  const effectiveOpen = filtering ? true : open;
  const childrenWithVisibility = filtering
    ? React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child, { forceVisible: selfMatch }) : child
      )
    : children;

  return (
    <>
      <Box data-tour={tourKey || undefined}>
        <CollapsibleMenuItemToggle
          icon={icon}
          iconOnly={iconOnly}
          isActive={isActive}
          label={label}
          noLink={noLink}
          onClick={toggleCollapse}
          open={effectiveOpen}
          path={path}
        />
      </Box>
      <Collapse in={effectiveOpen && !iconOnly} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {childrenWithVisibility}
        </List>
      </Collapse>
    </>
  );
}

CollapsibleMenuItem.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  icon: PropTypes.object.isRequired,
  iconOnly: PropTypes.bool,
  label: PropTypes.string.isRequired,
  noLink: PropTypes.bool,
  path: PropTypes.string,
  tourKey: PropTypes.string
};

export default CollapsibleMenuItem;
